import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, FileDown } from "lucide-react";
import { SignaturePad } from "../components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "w-full bg-white border border-[#E3DECE] px-4 py-3 text-sm focus:outline-none focus:border-[#BA6D52] transition-colors";
const LABEL = "block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2";

const ANLAESSE = [
  "Geburtstag", "Hochzeit / Standesamt", "Familienfeier", "Dinner mit Freunden",
  "Firmenfeier im privaten Rahmen", "Jubiläum", "Weihnachten / Silvester", "Sonstiges",
];

export default function VermittlungPrivat() {
  const [stamm, setStamm] = useState({ bereiche: {}, positionen: {}, provision_rate_eur: 40, mwst_percent: 19 });
  const [form, setForm] = useState({
    betrieb: "", ansprechpartner: "", email: "", telefon: "",
    strasse: "", plz: "", ort: "", land: "DE",
    von_datum: "", bis_datum: "", einsatztage: 1, anzahl_koeche: 1,
    taeglich_von: "", taeglich_bis: "",
    anlass: "Geburtstag", personen: 8, beschreibung: "", budget_stundensatz_eur: "",
    agb_consent: false, widerruf_consent: false,
    signature_name: "", signature_ort: "", signature_data_url: "",
  });
  const [bereiche, setBereiche] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [signNow, setSignNow] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vermittlung/stammdaten`).then((r) => setStamm(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.von_datum) return;
    setForm((f) => (f.bis_datum ? f : { ...f, bis_datum: f.von_datum }));
  }, [form.von_datum]);

  useEffect(() => {
    if (!form.von_datum || !form.bis_datum) return;
    const a = new Date(form.von_datum), b = new Date(form.bis_datum);
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return;
    const days = Math.round((b - a) / 86400000) + 1;
    setForm((f) => (f.einsatztage === days ? f : { ...f, einsatztage: days }));
  }, [form.von_datum, form.bis_datum]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const isAT = form.land === "AT";
  const pct = Number(stamm.mwst_percent || 19);
  const rateNet = Number(stamm.provision_rate_eur || 40);
  const rateGross = rateNet * (1 + pct / 100);
  const totalGross = rateGross * Number(form.einsatztage || 1) * Number(form.anzahl_koeche || 1);
  const eur = (n) => n.toFixed(2).replace(".", ",");

  const submit = async (e) => {
    e.preventDefault();
    if (!bereiche.length) return toast.error("Bitte mindestens einen Bereich auswählen");
    if (!form.agb_consent) return toast.error("Bitte die Datenschutzhinweise bestätigen");
    if (!form.widerruf_consent)
      return toast.error("Bitte den Hinweis zum Widerrufsrecht bestätigen, damit ich sofort vermitteln darf");
    const plzLen = isAT ? 4 : 5;
    if (!/^\d+$/.test(form.plz.trim()) || form.plz.trim().length !== plzLen)
      return toast.error(`Bitte eine ${plzLen}-stellige Postleitzahl für ${isAT ? "Österreich" : "Deutschland"} angeben`);
    if (signNow && (!form.signature_data_url || !form.signature_name.trim()))
      return toast.error("Bitte Namen eintragen und im Feld unterschreiben — oder das Häkchen entfernen");
    setBusy(true);
    try {
      const payload = { ...form, bereiche, customer_type: "private", placement_type: "temporary",
        betriebsart: "Privathaushalt" };
      payload.ansprechpartner = form.ansprechpartner || form.betrieb;
      if (!signNow) {
        payload.signature_name = "";
        payload.signature_ort = "";
        payload.signature_data_url = "";
      }
      ["einsatztage", "anzahl_koeche", "personen"].forEach((k) => (payload[k] = Number(payload[k]) || 1));
      payload.budget_stundensatz_eur = payload.budget_stundensatz_eur === "" ? null : Number(payload.budget_stundensatz_eur);
      ["von_datum", "bis_datum"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      const r = await axios.post(`${API}/vermittlung/anfragen`, payload);
      setDone(r.data.request_number);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anfrage konnte nicht gesendet werden");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 md:py-32" data-testid="privat-request-success">
        <Check className="w-10 h-10 text-[#BA6D52]" strokeWidth={1.4} />
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-8">Anfrage ist raus.</h1>
        <p className="text-[#575349] mt-6 leading-relaxed">
          Vielen Dank — Ihr Vorgang <b className="text-[#2C2A26]">{done}</b> liegt mir vor. Ich suche jetzt
          einen passenden Koch aus meinem Netzwerk und melde mich innerhalb von 24 Stunden.
          Eine Eingangsbestätigung ist gerade unterwegs.
        </p>
        <p className="text-sm text-[#757067] mt-6 leading-relaxed">
          Bei Fragen erreichen Sie mich direkt: +49 151 27282560.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="privat-request-page">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Für Privatkunden & Familien</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">Privatkoch anfragen</h1>
      <p className="text-[#757067] font-light text-lg mt-6 leading-relaxed">
        Sie möchten zu Hause bekocht werden? Sagen Sie mir Anlass, Termin und Personenzahl — ich suche
        einen passenden selbstständigen Koch aus meinem Netzwerk. Honorar und Einkauf rechnet der Koch
        direkt mit Ihnen ab, von mir kommt nur die Vermittlungsprovision.
      </p>
      <div className="mt-6 border-l-2 border-[#BA6D52] bg-[#FAF5F1] px-5 py-4" data-testid="privat-eigener-service">
        <p className="text-sm text-[#575349] leading-relaxed">
          Sie möchten, dass <b className="text-[#2C2A26]">ich selbst</b> für Sie koche? Das ist ein eigenes
          Angebot mit festen Menüpreisen — hier entlang:{" "}
          <a href="/privatkoch" className="text-[#BA6D52] underline">Privatkoch Lukas Stutz</a>.
        </p>
      </div>

      <form onSubmit={submit} className="mt-14 space-y-12">
        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Ihr Anlass</h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div>
              <label className={LABEL}>Anlass</label>
              <select className={FIELD} value={form.anlass} onChange={set("anlass")} data-testid="privat-anlass">
                {ANLAESSE.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div><label className={LABEL}>Personen *</label>
              <input type="number" min="1" className={FIELD} required value={form.personen}
                onChange={set("personen")} data-testid="privat-personen" /></div>
            <div><label className={LABEL}>Termin *</label>
              <input type="date" className={FIELD} required value={form.von_datum}
                onChange={set("von_datum")} data-testid="privat-von" /></div>
            <div><label className={LABEL}>Letzter Tag (bei mehreren)</label>
              <input type="date" className={FIELD} value={form.bis_datum}
                onChange={set("bis_datum")} data-testid="privat-bis" /></div>
            <div><label className={LABEL}>Von (Uhrzeit)</label>
              <input type="time" className={FIELD} value={form.taeglich_von}
                onChange={set("taeglich_von")} data-testid="privat-zeit-von" /></div>
            <div><label className={LABEL}>Bis (Uhrzeit)</label>
              <input type="time" className={FIELD} value={form.taeglich_bis}
                onChange={set("taeglich_bis")} data-testid="privat-zeit-bis" /></div>
            <div><label className={LABEL}>Anzahl Köche</label>
              <input type="number" min="1" className={FIELD} value={form.anzahl_koeche}
                onChange={set("anzahl_koeche")} data-testid="privat-anzahl-koeche" /></div>
            <div><label className={LABEL}>Ihr Budget für den Koch (€/Stunde)</label>
              <input type="number" step="0.5" className={FIELD} value={form.budget_stundensatz_eur}
                onChange={set("budget_stundensatz_eur")} data-testid="privat-budget" /></div>
          </div>

          <p className={`${LABEL} mt-8`}>Welche Küche schwebt Ihnen vor? *</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stamm.bereiche || {}).map(([k, label]) => (
              <button type="button" key={k}
                onClick={() => setBereiche(bereiche.includes(k) ? bereiche.filter((x) => x !== k) : [...bereiche, k])}
                data-testid={`privat-bereich-${k}`}
                className={`px-4 py-2 text-xs border transition-colors ${bereiche.includes(k) ? "bg-[#BA6D52] text-white border-[#BA6D52]" : "bg-white border-[#E3DECE] text-[#575349] hover:border-[#BA6D52]"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            <label className={LABEL}>Wünsche & Besonderheiten</label>
            <textarea rows={4} className={FIELD} value={form.beschreibung} onChange={set("beschreibung")}
              data-testid="privat-beschreibung"
              placeholder="Menüwünsche, Allergien und Unverträglichkeiten, Küchenausstattung, Service am Tisch …" />
            <p className="text-[11px] text-[#757067] mt-2 leading-relaxed">
              Allergien und Unverträglichkeiten bitte hier nennen — ich gebe sie an den Koch weiter.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Ihre Kontaktdaten</h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div><label className={LABEL}>Vor- und Nachname *</label>
              <input className={FIELD} required value={form.betrieb} onChange={set("betrieb")} data-testid="privat-name" /></div>
            <div><label className={LABEL}>Telefon *</label>
              <input className={FIELD} required value={form.telefon} onChange={set("telefon")} data-testid="privat-telefon" /></div>
            <div className="sm:col-span-2"><label className={LABEL}>E-Mail *</label>
              <input type="email" className={FIELD} required value={form.email} onChange={set("email")} data-testid="privat-email" /></div>
            <div className="sm:col-span-2"><label className={LABEL}>Straße & Hausnummer (Einsatzort)</label>
              <input className={FIELD} value={form.strasse} onChange={set("strasse")} data-testid="privat-strasse" /></div>
            <div>
              <label className={LABEL}>Land *</label>
              <select className={FIELD} value={form.land} onChange={set("land")} data-testid="privat-land">
                <option value="DE">Deutschland</option>
                <option value="AT">Österreich</option>
              </select>
            </div>
            <div><label className={LABEL}>PLZ * ({isAT ? "4" : "5"}-stellig)</label>
              <input className={FIELD} required inputMode="numeric" maxLength={isAT ? 4 : 5}
                value={form.plz} onChange={set("plz")} data-testid="privat-plz" /></div>
            <div className="sm:col-span-2"><label className={LABEL}>Ort *</label>
              <input className={FIELD} required value={form.ort} onChange={set("ort")} data-testid="privat-ort" /></div>
          </div>
        </section>

        <div className="bg-[#F2EFE9] p-6" data-testid="privat-provision-preview">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#BA6D52]">Meine Vermittlungsprovision</p>
          <p className="text-sm text-[#575349] mt-3 leading-relaxed">
            <b className="text-[#2C2A26]">{eur(rateGross)} € inkl. {pct} % MwSt.</b> ({eur(rateNet)} € netto)
            je Koch und Einsatztag — bei {form.einsatztage || 1} Tag(en) und {form.anzahl_koeche || 1} Koch/Köchen
            also <b className="text-[#2C2A26]">{eur(totalGross)} € inkl. MwSt.</b>
          </p>
          <p className="text-xs text-[#757067] mt-3 leading-relaxed">
            Das Honorar des Kochs, Einkauf und Anfahrt vereinbaren Sie direkt mit ihm. Kommt keine
            Vermittlung zustande, entstehen Ihnen keine Kosten. Kein Abo, keine Grundgebühr, keine Bindung.
          </p>
          <a href={`${API}/vermittlung/vertrag/betrieb.pdf`} target="_blank" rel="noreferrer"
            data-testid="privat-vertrag-download"
            className="inline-flex items-center gap-2 mt-4 border border-[#BA6D52] text-[#BA6D52] hover:bg-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors">
            <FileDown className="w-4 h-4" /> Vereinbarung lesen
          </a>

          <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer mt-6 pt-6 border-t border-[#E3DECE]">
            <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={signNow}
              onChange={(e) => setSignNow(e.target.checked)} data-testid="privat-sign-now" />
            <span>
              Ich möchte die Vermittlungsvereinbarung <b className="text-[#2C2A26]">jetzt direkt digital
              unterschreiben</b> — dann erhalte ich sie sofort unterschrieben als PDF per E-Mail.
            </span>
          </label>

          {signNow && (
            <div className="mt-6 space-y-4" data-testid="privat-sign-block">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Name der unterzeichnenden Person *</label>
                  <input className={FIELD} value={form.signature_name} onChange={set("signature_name")}
                    placeholder="Vor- und Nachname" data-testid="privat-sign-name" />
                </div>
                <div>
                  <label className={LABEL}>Ort</label>
                  <input className={FIELD} value={form.signature_ort} onChange={set("signature_ort")}
                    placeholder={form.ort || "Ort"} data-testid="privat-sign-ort" />
                </div>
              </div>
              <SignaturePad
                onChange={(v) => setForm((f) => ({ ...f, signature_data_url: v }))}
                testId="privat-signature"
              />
            </div>
          )}
        </div>

        <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
          <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.widerruf_consent}
            onChange={set("widerruf_consent")} data-testid="privat-widerruf" />
          <span>
            Ich wünsche ausdrücklich, dass <b className="text-[#2C2A26]">sofort mit der Vermittlung begonnen
            wird</b>. Mir ist bekannt, dass mein 14-tägiges Widerrufsrecht erlischt, sobald die Vermittlung
            vollständig erbracht ist. Die{" "}
            <a href="/agb" target="_blank" rel="noreferrer" className="text-[#BA6D52] underline">Widerrufsbelehrung</a>{" "}
            habe ich gelesen. *
          </span>
        </label>

        <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
          <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.agb_consent}
            onChange={set("agb_consent")} data-testid="privat-dsgvo" />
          <span>
            Ich habe die <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#BA6D52] underline">Datenschutzhinweise</a> gelesen
            und bin mit der Verarbeitung meiner Angaben zum Zweck der Vermittlung einverstanden. *
          </span>
        </label>

        <button type="submit" disabled={busy} data-testid="privat-submit"
          className="w-full sm:w-auto bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-10 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors">
          {busy ? "Wird gesendet …" : "Anfrage senden"}
        </button>
      </form>
    </div>
  );
}
